import { FC } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { LIBX_SORT_BY } from '../../../shared/common/SortByDropDown';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import ProductList from '../../../shared/common/ProductList';
import { getPaginationStateObject, Pagination } from '../../../shared/common/Pagination';
import { FilterBar } from '../../../shared/common/FilterBar';
import { updateProductInProductsList } from '../../../store/libx/actions';
import LibxchangeProductElement from './LibxchangeProductElement';
import ContentBaseLayout from './ContentBaseLayout';
import { DISPLAY_TYPE_TILE } from '../../../store/displayType/types';
import { useHistory } from 'react-router';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { useAddLibxProductToCollection } from '../productdetail/api/useAddLibxProductToCollection';
import { invalidateNewlyAddedProducts } from '../api/useNewlyAddedProducts';
import { ProductOut } from '../../../store/api';
import { invalidateAllTitlesProducts } from '../api/useAllTitlesProducts';
import { AggregationsProps, ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';

const StFilterBar = styled(FilterBar)`
    margin: 10px 0 40px 0;
`;

type Props = AggregationsProps & {
    products: {
        count: number;
        list: ProductOut[];
    };
    loading: boolean;
    documentTitle: string;
    displayFilter: boolean;
    pageQueryParam: string;
    sortByQueryParam: string;
    displayType: string;
    pageSize: number;
    sortBy: string;
    page: number;
    isNewlyAdded: boolean;
    isAllTitles: boolean;
};

export const LibxChangeProductPageBuilder: FC<Props> = props => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { mutate: addToCollectionMutation } = useAddLibxProductToCollection();
    const {
        products,
        loading,
        documentTitle,
        displayFilter,
        pageQueryParam,
        sortByQueryParam,
        displayType,
        pageSize,
        sortBy,
        page,
        isNewlyAdded,
        isAllTitles
    } = props;

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: sortByQueryParam, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            pageQueryParam,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(history.location.search, 'display', nextDisplayType, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    function onUpdateProductInProductsList(id) {
        dispatch(updateProductInProductsList(id));
    }

    function onAddToCollection(productId) {
        addToCollectionMutation(productId);
        if (isNewlyAdded) {
            invalidateNewlyAddedProducts().then();
        }
        if (isAllTitles) {
            invalidateAllTitlesProducts().then();
        }
    }

    return (
        <ContentBaseLayout
            topComponent={
                <StFilterBar
                    totalProducts={products.count}
                    hideGroupBy={true}
                    hideSelectButtons={true}
                    excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                    sorting={LIBX_SORT_BY}
                    onSortChange={setSortBy}
                    onPageChange={setPage}
                    displayType={displayType}
                    onFilterDisplayToggle={onFilterDisplayToggle}
                    onDisplayChange={onDisplayChange}
                    currentPage={page}
                    pageSize={pageSize}
                    sortBy={sortBy}
                    displayFilter={displayFilter}
                />
            }
            sidebarComponent={
                displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={props.visibleFilterGroups}
                        buildQueryKey={props.buildQueryKey}
                        fetchAggregations={props.fetchAggregations}
                        fetchAggregationsQuickSearch={props.fetchAggregationsQuickSearch}
                    />
                ) : null
            }
            contentComponent={
                products.list.length === 0 && !props.loading ? (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                ) : (
                    <div className="ca-main">
                        <div className="ca-main-inner">
                            <ProductList
                                products={products.list}
                                displayType={displayType}
                                listItemComponent={LibxchangeProductElement}
                                tileItemComponent={LibxchangeProductElement}
                                onAddToCollection={onAddToCollection}
                                updateProduct={onUpdateProductInProductsList}
                                currentPage={props.page}
                            />
                            <Pagination totalItems={products.count} currentPage={props.page} pageSize={55} />
                        </div>
                    </div>
                )
            }
            documentTitle={documentTitle}
            loading={loading}
        />
    );
};
